/**
 * The route guard.
 *
 * It guards *navigation*, not data: the API authenticates every request on its
 * own and would refuse an unauthenticated one regardless. What this stops is a
 * signed-out person landing on a screen that can only render errors.
 *
 * `loading` renders a spinner rather than redirecting, because redirecting on
 * an unresolved session would bounce every authenticated user to `/login` on
 * every hard refresh.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { FullPageLoader } from '../components/FullPageLoader';
import { useAuth } from './auth-context';

export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <FullPageLoader label="Checking your session" />;
  }

  if (status === 'unauthenticated') {
    // `from` is carried in history state, never in the query string: a return
    // path is not sensitive, but keeping it out of the URL keeps it out of the
    // referrer and the API's access logs too.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
}
